import ajax from '../api/ajax';
import moment from "moment";
import encrypt from "@/assets/js/encrypt-DES";
import api from "@/api/url";
import i18n from "@/lang/index";
import activityValidator from "./activity-validator";
import loginTitleImg from "@/assets/img/icontitle_1.png";
import { $customMessage } from "@/plugins/customMessage";
import store from "@/store";
class SessionManagerService {
    constructor() {
        this.heartbeatTimer = null
        this.isActive = false
        this.inactivityTimeout = 30 * 60 * 1000
        this.LAST_ACTIVITY_KEY = 'lastActivityTime'
        this.USERNAME_KEY = 'currentUsername'
        this.SESSION_EXPIRED_KEY = 'sessionExpired'
        this.PROJECT_REFRESH_TOKEN_KEY = 'projectRefreshToken'
        this.PROJECT_REFRESH_PENDING_KEY = 'projectRefreshPending'
        this.activityListeners = []
        this.isListenersActive = false
        this.keyboardBlocker = null
        this.beforeUnloadHandler = null
        this.isModalShowing = false
        this.scrollThrottleTimer = null
        this.modalEventListeners = []
        this.passwordChangedLoginPromise = null
        this.resolvePasswordChangedLogin = null
        this.rejectPasswordChangedLogin = null
        this.reloginUsername = ''
        this.reloginToken = ''
        this.isProjectRefreshRequired = false

        this.restoreProjectRefreshToken()

        this.checkSessionOnLoad()
    }


    getLastActivity() {
        const stored = this.safeSessionStorageGet(this.LAST_ACTIVITY_KEY)
        if (!stored) {
            return Date.now()
        }


        const activityData = activityValidator.parseStoredData(stored)
        if (!activityData) {
            console.warn('Invalid activity data format, possible tampering detected')
            return 0
        }


        if (activityData.legacy) {
            console.log('Legacy activity data detected, will update on next set')
            return activityData.timestamp
        }


        const currentUsername = this.getCurrentUsername()
        if (!activityValidator.validateActivityData(activityData, currentUsername)) {
            console.warn('Activity data validation failed, possible tampering detected')
            this.safeSessionStorageRemove(this.LAST_ACTIVITY_KEY)
            return 0
        }

        return activityData.timestamp
    }


    setLastActivity(value) {
        // const formattedTime = moment().format('YYYY-MM-DD HH:mm:ss');
        // console.log('Page Refresh time： '+ formattedTime)


        const currentUsername = this.getCurrentUsername()
        const secureData = activityValidator.createSecureActivityData(value, currentUsername)


        const dataToStore = JSON.stringify(secureData)
        this.safeSessionStorageSet(this.LAST_ACTIVITY_KEY, dataToStore)
    }


    setCurrentUsername(username) {
        this.safeSessionStorageSet(this.USERNAME_KEY, username)
    }


    getCurrentUsername() {
        return this.safeSessionStorageGet(this.USERNAME_KEY) || ''
    }


    checkSessionOnLoad() {

        setTimeout(() => {
            const isExpired = this.safeSessionStorageGet(this.SESSION_EXPIRED_KEY)
            if (isExpired === 'true') {

                console.log('Session was expired, redirecting to login')
                this.clearUserData()

                setTimeout(() => {
                    window.location.hash = "/login";
                }, 0)
            }
        }, 100)
    }


    markSessionExpired() {
        this.safeSessionStorageSet(this.SESSION_EXPIRED_KEY, 'true')
    }


    clearSessionExpiredFlag() {
        this.safeSessionStorageRemove(this.SESSION_EXPIRED_KEY)
    }


    restoreProjectRefreshToken() {
        if (this.safeSessionStorageGet(this.PROJECT_REFRESH_PENDING_KEY) !== 'true') {
            return false
        }

        const token = this.safeSessionStorageGet(this.PROJECT_REFRESH_TOKEN_KEY)
        if (!token) {
            this.completeProjectRefresh()
            return false
        }

        if (!this.safeSessionStorageSet('token', token)) {
            return false
        }

        this.clearSessionExpiredFlag()
        this.setLastActivity(Date.now())
        this.completeProjectRefresh()
        return true
    }

    completeProjectRefresh() {
        if (this.safeSessionStorageGet(this.PROJECT_REFRESH_PENDING_KEY) !== 'true') {
            return
        }

        this.safeSessionStorageRemove(this.PROJECT_REFRESH_PENDING_KEY)
        this.safeSessionStorageRemove(this.PROJECT_REFRESH_TOKEN_KEY)
    }


    safeSessionStorageSet(key, value) {
        try {
            sessionStorage.setItem(key, value)
            return true
        } catch (error) {
            console.warn('Failed to set sessionStorage item:', key, error)
            return false
        }
    }

    isLoginPage() {
        const hashPath = (window.location.hash || '').replace(/^#/, '').split('?')[0]
        return hashPath === '' || hashPath === '/' || hashPath === '/login' || hashPath === '/dashboard/login'
    }

    safeSessionStorageGet(key) {
        try {
            return sessionStorage.getItem(key)
        } catch (error) {
            console.warn('Failed to get sessionStorage item:', key, error)
            return null
        }
    }

    safeSessionStorageRemove(key) {
        try {
            sessionStorage.removeItem(key)
            return true
        } catch (error) {
            console.warn('Failed to remove sessionStorage item:', key, error)
            return false
        }
    }


    getI18nText(key, fallback) {
        try {
            return i18n.t(key) || fallback
        } catch (error) {
            console.warn('i18n translation failed for key:', key, error)
            return fallback
        }
    }

    start() {
        if (this.isActive) return
        this.isActive = true
        const timeSinceLastActivity = Date.now() - this.getLastActivity()
        if (timeSinceLastActivity > this.inactivityTimeout) {
            console.log('Session expired due to inactivity： '+ timeSinceLastActivity)
            this.handleInactivity()
            return
        }
        this.setLastActivity(Date.now())
        this.bindActivityListeners()
        console.log('Listener is starting up...')
    }

    stop() {

        if (this.isModalShowing) {
            this.closeLoginModal()
        }

        this.isActive = false
        this.safeSessionStorageRemove(this.LAST_ACTIVITY_KEY)
        this.unbindActivityListeners()


        if (this.scrollThrottleTimer) {
            clearTimeout(this.scrollThrottleTimer)
            this.scrollThrottleTimer = null
        }

        if (this.heartbeatTimer) {
            clearTimeout(this.heartbeatTimer)
            this.heartbeatTimer = null
        }

        console.log('Listener is stopping...')
    }

    checkActivity() {
        if (!this.validateActivityIntegrity()) {
            console.warn('Activity integrity check failed, forcing session timeout due to tampering')
            return true // 强制超时，触发登录框
        }

        const timeSinceLastActivity = Date.now() - this.getLastActivity()
        // console.log('Time since last activity:', timeSinceLastActivity)

        if (timeSinceLastActivity > this.inactivityTimeout) {
            return true
        } else {
            this.setLastActivity(Date.now())
            return false
        }
    }


    validateActivityIntegrity() {
        const stored = this.safeSessionStorageGet(this.LAST_ACTIVITY_KEY)
        if (!stored) {
            return true
        }

        const activityData = activityValidator.parseStoredData(stored)
        if (!activityData) {
            console.error('Activity data parsing failed - possible tampering')
            return false
        }


        if (activityData.legacy) {
            return true
        }

        const currentUsername = this.getCurrentUsername()
        const isValid = activityValidator.validateActivityData(activityData, currentUsername)

        if (!isValid) {
            console.error('Activity data signature validation failed - tampering detected')
        }

        return isValid
    }


    detectTampering() {
        return !this.validateActivityIntegrity()
    }


    recordActivity(event) {
        if (this.isModalShowing) {
            if (event) {
                event.preventDefault()
                event.stopPropagation()
                event.stopImmediatePropagation()
            }
            return
        }


        if (this.detectTampering()) {
            console.error('Tampering detected in recordActivity, forcing session timeout')
            this.handleInactivity()
            if (event) {
                event.preventDefault()
                event.stopPropagation()
                event.stopImmediatePropagation()
            }
            return
        }

        const isExpired = this.checkActivity()
        if (isExpired) {
            this.handleInactivity()
            if (event) {
                // 会话超时时阻止事件继续传播
                event.preventDefault()
                event.stopPropagation()
                event.stopImmediatePropagation()
            }
        }
    }

    bindActivityListeners() {
        if (this.isListenersActive) return


        const throttledScrollListener = (event) => {
            if (this.scrollThrottleTimer) return
            this.scrollThrottleTimer = setTimeout(() => {
                this.recordActivity(event)
                this.scrollThrottleTimer = null
            }, 200)
        }

        const events = [
            { name: 'keydown', listener: this.recordActivity.bind(this) },
            { name: 'scroll', listener: throttledScrollListener },
            { name: 'touchstart', listener: this.recordActivity.bind(this) },
            { name: 'click', listener: this.recordActivity.bind(this) }
        ]

        this.activityListeners = []
        events.forEach(({ name, listener }) => {
            document.addEventListener(name, listener, {
                capture: true
            })

            this.activityListeners.push({ event: name, listener })
        })

        this.isListenersActive = true
        console.log('Activity listeners bound')
    }

    unbindActivityListeners() {
        if (!this.isListenersActive) return

        this.activityListeners.forEach(({ event, listener }) => {
            document.removeEventListener(event, listener, {
                capture: true
            })
        })

        // 清理节流定时器
        if (this.scrollThrottleTimer) {
            clearTimeout(this.scrollThrottleTimer)
            this.scrollThrottleTimer = null
        }

        this.activityListeners = []
        this.isListenersActive = false
        console.log('Activity listeners unbound')
    }


    disablePageControls() {

        this.keyboardBlocker = (e) => {
            const key = e.key || this.getKeyFromKeyCode(e.keyCode)

            if (e.ctrlKey) {
                switch (key.toLowerCase()) {
                    case 'w':
                    case 'u':
                    case 'p':
                    case 's':
                    case 't':
                    case 'l':
                    case 'n':
                        e.preventDefault()
                        e.stopPropagation()
                        return false
                }
            }


            if (e.ctrlKey && e.shiftKey) {
                switch (key.toLowerCase()) {
                    case 'i':
                    case 'j':
                    case 'c':
                        e.preventDefault()
                        e.stopPropagation()
                        return false
                }
            }


            if (e.altKey) {
                switch (key.toLowerCase()) {
                    case 'arrowleft':
                    case 'arrowright':
                    case 'f4':
                    case 'd':
                        e.preventDefault()
                        e.stopPropagation()
                        return false
                }
            }


            if (key === 'F12' || e.keyCode === 123) {
                e.preventDefault()
                e.stopPropagation()
                return false
            }
        }


        document.addEventListener('keydown', this.keyboardBlocker, true)


        document.addEventListener('contextmenu', this.preventDefaultAction, true)


        history.pushState(null, null, location.href)
        window.addEventListener('popstate', this.preventBackNavigation)

        console.log('Page controls disabled')
    }


    getKeyFromKeyCode(keyCode) {
        const keyMap = {
            8: 'Backspace',
            9: 'Tab',
            13: 'Enter',
            16: 'Shift',
            17: 'Control',
            18: 'Alt',
            27: 'Escape',
            32: ' ',
            37: 'ArrowLeft',
            38: 'ArrowUp',
            39: 'ArrowRight',
            40: 'ArrowDown',
            46: 'Delete',
            65: 'a', 66: 'b', 67: 'c', 68: 'd', 69: 'e', 70: 'f', 71: 'g', 72: 'h',
            73: 'i', 74: 'j', 75: 'k', 76: 'l', 77: 'm', 78: 'n', 79: 'o', 80: 'p',
            81: 'q', 82: 'r', 83: 's', 84: 't', 85: 'u', 86: 'v', 87: 'w', 88: 'x',
            89: 'y', 90: 'z',
            112: 'F1', 113: 'F2', 114: 'F3', 115: 'F4', 116: 'F5', 117: 'F6',
            118: 'F7', 119: 'F8', 120: 'F9', 121: 'F10', 122: 'F11', 123: 'F12'
        }
        return keyMap[keyCode] || 'Unknown'
    }


    enablePageControls() {

        if (this.keyboardBlocker) {
            document.removeEventListener('keydown', this.keyboardBlocker, true)
            this.keyboardBlocker = null
        }


        document.removeEventListener('contextmenu', this.preventDefaultAction, true)



        window.removeEventListener('popstate', this.preventBackNavigation)

        console.log('Page controls enabled')
    }

    preventDefaultAction = (e) => {
        e.preventDefault()
        e.stopPropagation()
        return false
    }

    preventBackNavigation = (e) => {
        history.pushState(null, null, location.href)
        e.preventDefault()
        e.stopPropagation()
        return false
    }

    handleInactivity() {
        if (this.isLoginPage()) {
            this.stop()
            return
        }
        // 防止重复弹框
        if (this.isModalShowing) {
            console.log('Login modal is already showing, skipping...')
            return
        }


        const isTampering = this.detectTampering()
        if (isTampering) {
            console.error('Session timeout due to data tampering detected')
        } else {
            console.log('User inactive for timeout period, showing login modal')
        }


        this.markSessionExpired()

        this.unbindActivityListeners()
        this.showLoginModal(isTampering)
    }

    handleTokenExpired() {
        console.log('Token expired, redirecting to login')
        this.stop()
        this.clearUserData()
        window.location.hash = "/login";
    }

    clearUserData() {
        this.reloginToken = ''
        this.isProjectRefreshRequired = false
        this.safeSessionStorageRemove(this.PROJECT_REFRESH_PENDING_KEY)
        this.safeSessionStorageRemove(this.PROJECT_REFRESH_TOKEN_KEY)
        this.safeSessionStorageRemove("menu")
        this.safeSessionStorageRemove("menuActive")
        this.safeSessionStorageRemove("userMenuJson")
        this.safeSessionStorageRemove("token")
        this.safeSessionStorageRemove("environment")
        this.safeSessionStorageRemove("localData")
        this.safeSessionStorageRemove("pageSize")
        this.safeSessionStorageRemove("systemParam")
        this.safeSessionStorageRemove(this.LAST_ACTIVITY_KEY)
        this.safeSessionStorageRemove(this.USERNAME_KEY)
        this.safeSessionStorageRemove(this.SESSION_EXPIRED_KEY)
        this.safeSessionStorageRemove("logout")
        this.safeSessionStorageRemove("domain")
        activityValidator.clearCache()
    }

    showPasswordChangedModal(message = null) {
        if (this.isLoginPage()) {
            return Promise.reject(new Error('Already on login page'))
        }

        if (this.passwordChangedLoginPromise) {
            return this.passwordChangedLoginPromise
        }

        if (this.isModalShowing) {
            return Promise.reject(new Error('Login modal is already showing'))
        }

        const localizedMessage = message || this.getI18nText(
          'sessionModal.passwordChangedMessage',
          'Password has changed, please login again.'
        )

        this.passwordChangedLoginPromise = new Promise((resolve, reject) => {
            this.resolvePasswordChangedLogin = resolve
            this.rejectPasswordChangedLogin = reject
        })

        this.markSessionExpired()
        this.unbindActivityListeners()
        this.showLoginModal(false, localizedMessage)

        return this.passwordChangedLoginPromise
    }

    completePasswordChangedLogin() {
        if (this.resolvePasswordChangedLogin) {
            this.resolvePasswordChangedLogin()
        }
        this.clearPasswordChangedLoginPromise()
    }

    cancelPasswordChangedLogin() {
        if (this.rejectPasswordChangedLogin) {
            this.rejectPasswordChangedLogin(new Error('Re-login was cancelled'))
        }
        this.clearPasswordChangedLoginPromise()
    }

    clearPasswordChangedLoginPromise() {
        this.passwordChangedLoginPromise = null
        this.resolvePasswordChangedLogin = null
        this.rejectPasswordChangedLogin = null
    }
    showLoginModal(isTampering = false, messageOverride = null) {

        if (this.isLoginPage()) {
            this.stop()
            return
        }
        if (this.isModalShowing) {
            console.log('Login modal is already showing')
            return
        }


        const existingOverlay = document.getElementById('session-lock-overlay')
        if (existingOverlay) {
            console.log('Login modal overlay already exists in DOM')
            return
        }


        this.isModalShowing = true


        this.disablePageControls()

        const overlay = document.createElement('div')
        overlay.id = 'session-lock-overlay'
        overlay.className = 'session-lock-overlay'


        const loginModal = document.createElement('div')
        loginModal.className = 'session-lock-modal'


        const titleText = isTampering
          ? this.getI18nText('sessionModal.securityTitle', 'Security Alert')
          : this.getI18nText('sessionModal.title', 'Session Expired')

        const messageText = messageOverride || (isTampering
          ? this.getI18nText('sessionModal.securityMessage', 'Unauthorized data modification detected. Please login again for security.')
          : this.getI18nText('sessionModal.message', 'Please login again to continue'))

        const titleClassName = isTampering
          ? 'session-lock-title session-lock-title--tampering'
          : 'session-lock-title'
        const warningMarkup = isTampering
          ? `<p class="session-lock-warning">⚠️ ${this.getI18nText('sessionModal.securityWarning', 'For your security, please verify your identity')}</p>`
          : ''

        this.reloginUsername = this.getCurrentUsername()

        loginModal.innerHTML = `
            <div class="session-lock-header">
                <img
                    src="${loginTitleImg}"
                    alt="title"
                    class="session-lock-logo"
                />
                <h3 class="${titleClassName}">${titleText}</h3>
                <p class="session-lock-message">${messageText}</p>
                ${warningMarkup}
            </div>
            <form id="relogin-form" class="session-lock-form" autocomplete="off" novalidate>
                <div class="session-lock-field">
                    <input type="text" id="relogin-username" name="relogin-username"
                           placeholder="${this.getI18nText('sessionModal.username', 'Username')}"
                           autocomplete="off"
                           class="session-lock-input session-lock-input--readonly" 
                           readonly>
                </div>
                <div class="session-lock-field session-lock-field--last${sessionStorage.getItem('domain') === 'true' ? ' session-lock-hidden' : ''}">
                    <input type="password" id="relogin-password" name="relogin-password"
                           placeholder="${this.getI18nText('sessionModal.password', 'Password')}"
                           autocomplete="off"
                           class="session-lock-input">
                    <div id="login-error-message" class="session-lock-error" hidden></div>
                </div>
                <div class="session-lock-actions">
                    <button type="submit" id="login-submit-btn" class="session-lock-button session-lock-button--primary${sessionStorage.getItem('domain') === 'true' ? ' session-lock-hidden' : ''}">
                        ${this.getI18nText('sessionModal.loginButton', 'Login')}
                    </button>
                    <button type="button" id="logout-btn" class="session-lock-button session-lock-button--secondary">
                        ${this.getI18nText('sessionModal.logoutButton', 'Logout')}
                    </button>
                </div>
            </form>
        `

        overlay.appendChild(loginModal)
        document.body.appendChild(overlay)

        const usernameInput = loginModal.querySelector('#relogin-username')
        if (usernameInput) {
            usernameInput.value = this.reloginUsername
        }

        const form = loginModal.querySelector('#relogin-form')
        if (form) {
            const formSubmitHandler = (e) => {
                e.preventDefault()
                this.handleRelogin()
            }
            form.addEventListener('submit', formSubmitHandler)
            this.modalEventListeners.push({ element: form, event: 'submit', handler: formSubmitHandler })
        }


        const logoutBtn = loginModal.querySelector('#logout-btn')
        if (logoutBtn) {
            const logoutClickHandler = () => {
                this.cancelPasswordChangedLogin()
                this.closeLoginModal()
                this.clearUserData()
                location.href = "/"
            }
            logoutBtn.addEventListener('click', logoutClickHandler)
            this.modalEventListeners.push({ element: logoutBtn, event: 'click', handler: logoutClickHandler })
        }


        document.body.classList.add('session-lock-body-open')


        setTimeout(() => {
            const passwordInput = loginModal.querySelector('#relogin-password')
            if (passwordInput) {
                passwordInput.focus()
            }
        }, 100)
    }


    closeLoginModal() {

        this.cancelPasswordChangedLogin()
        this.modalEventListeners.forEach(({ element, event, handler }) => {
            if (element) {
                element.removeEventListener(event, handler)
            }
        })
        this.modalEventListeners = []

        const overlay = document.getElementById('session-lock-overlay')
        if (overlay) {
            overlay.remove()
        }


        document.body.classList.remove('session-lock-body-open')


        this.enablePageControls()


        this.isModalShowing = false

        console.log('Login modal closed')
    }


    /** HTTP 401 + code 4002: JWT expired; show re-login form (same as password changed). */
    showTokenExpiredModal(msg) {
        if (this.isLoginPage()) {
            return
        }
        if (this.isModalShowing) {
            return
        }
        const message = msg || this.getI18nText('sessionModal.tokenExpiredMessage', 'Your session has expired. Please log in again.')
        this.markSessionExpired()
        this.unbindActivityListeners()
        this.showLoginModal(false, message)
    }

    /** HTTP 401 + code 4003: JWT invalid/malformed; show re-login form. */
    showTokenInvalidModal(msg) {
        if (this.isLoginPage()) {
            return
        }
        if (this.isModalShowing) {
            return
        }
        const message = msg || this.getI18nText('sessionModal.tokenInvalidMessage', 'Your authentication token is invalid. Please log in again.')
        this.markSessionExpired()
        this.unbindActivityListeners()
        this.showLoginModal(false, message)
    }

    /** HTTP 403 + code 4005: authenticated but lacks required role; show global error toast, no redirect. */
    showNoPermissionModal(msg) {
        const message = msg || this.getI18nText('sessionModal.noPermissionMessage', 'You do not have permission to access this resource.');
        $customMessage.error(message);
    }

    showProjectChangedModal() {
        if (document.getElementById('session-lock-overlay')) {
            return
        }

        const overlay = document.createElement('div')
        overlay.id = 'session-lock-overlay'
        overlay.className = 'session-lock-overlay'

        const projectModal = document.createElement('div')
        projectModal.className = 'session-lock-modal'
        projectModal.innerHTML = `
            <div class="session-lock-header">
                <img
                    src="${loginTitleImg}"
                    alt="title"
                    class="session-lock-logo"
                />
                <h3 class="session-lock-title">
                    ${this.getI18nText('sessionModal.projectChangedTitle', 'Project changed')}
                </h3>
                <p class="session-lock-message">
                    ${this.getI18nText('sessionModal.projectChangedMessage', 'The current project has changed. Refresh the page to load the correct project data.')}
                </p>
            </div>
            <div class="session-lock-actions session-lock-actions--right">
                <button type="button" id="project-refresh-btn" class="session-lock-button session-lock-button--primary">
                    ${this.getI18nText('sessionModal.reloadButton', 'Refresh')}
                </button>
                <button type="button" id="project-relogin-btn" class="session-lock-button session-lock-button--secondary">
                    ${this.getI18nText('sessionModal.logoutButton', 'Logout')}
                </button>
            </div>
        `

        overlay.appendChild(projectModal)
        document.body.appendChild(overlay)
        document.body.classList.add('session-lock-body-open')

        const refreshBtn = projectModal.querySelector('#project-refresh-btn')
        const reloginBtn = projectModal.querySelector('#project-relogin-btn')

        refreshBtn.addEventListener('click', () => {
            const token = this.reloginToken || this.safeSessionStorageGet('token')
            if (token) {
                this.safeSessionStorageSet(this.PROJECT_REFRESH_TOKEN_KEY, token)
                this.safeSessionStorageSet(this.PROJECT_REFRESH_PENDING_KEY, 'true')
                this.safeSessionStorageSet('token', token)
            }
            this.clearSessionExpiredFlag()
            this.setLastActivity(Date.now())
            window.location.reload()
        })

        reloginBtn.addEventListener('click', () => {
            this.cancelPasswordChangedLogin()
            this.closeLoginModal()
            this.clearUserData()
            location.href = "/"
        })
    }

    shouldPreserveTokenForProjectRefresh() {
        return this.isProjectRefreshRequired
    }

    async verifyCurrentProject() {
        const res = await ajax.get(api.CURRENTUSERPROJECT)
        if (!res || res.code != 200) {
            throw new Error((res && res.msg) || 'Failed to get current project')
        }

        const pageProject = store.state.projectCode == null ? '' : String(store.state.projectCode)
        const tokenProject = res.data == null ? '' : String(res.data)
        return pageProject === tokenProject
    }



    async handleRelogin() {
        const loginModal = document.querySelector('.session-lock-modal')
        const usernameInput = loginModal && loginModal.querySelector('#relogin-username')
        const passwordInput = loginModal && loginModal.querySelector('#relogin-password')
        const errorDiv = loginModal && loginModal.querySelector('#login-error-message')
        const submitBtn = loginModal && loginModal.querySelector('#login-submit-btn')

        if (!usernameInput || !passwordInput) {
            console.error('Login form elements not found')
            return
        }

        const username = this.reloginUsername
        const password = passwordInput.value

        if (usernameInput.value !== username) {
            usernameInput.value = username
        }


        if (errorDiv) {
            errorDiv.hidden = true
            errorDiv.textContent = ''
        }


        if (!password || password.trim() === '') {
            if (errorDiv) {
                errorDiv.textContent = this.getI18nText('sessionModal.passwordRequired', 'Password is required')
                errorDiv.hidden = false
            }
            return
        }


        if (submitBtn) {
            submitBtn.disabled = true
            submitBtn.textContent = this.getI18nText('sessionModal.loginLoading', 'Logging in...')
            submitBtn.classList.add('session-lock-button--loading')
        }

        try {
            let requestParam = {
                username: username,
                password: password
            }
            let utcTime = moment.utc().valueOf();
            requestParam.password = encrypt.encryptByDES(password,Math.floor(utcTime/ 30000).toString())
            requestParam.timestamp = utcTime;


            const loginPromise = ajax.noLoadingPostAjax(api.LOGINAPI + "?from=PC", requestParam, true)
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Request timeout')), 30000)
            })

            Promise.race([loginPromise, timeoutPromise]).then(async (res) => {
                if (res.code == 200) {

                    let token = res.data;
                    if (typeof token === 'string' && token.startsWith("Bearer ")) {
                        token = token.replace("Bearer ", "");
                    }

                    this.reloginToken = token
                    this.isProjectRefreshRequired = true
                    this.safeSessionStorageSet("token", token);

                    const isSameProject = await this.verifyCurrentProject()

                    this.clearSessionExpiredFlag()

                    if (!isSameProject) {
                        this.closeLoginModal()
                        this.showProjectChangedModal()
                        return
                    }

                    this.isProjectRefreshRequired = false
                    this.reloginToken = ''
                    this.completePasswordChangedLogin()


                    this.closeLoginModal()


                    this.setLastActivity(Date.now())


                    this.isActive = false
                    this.start()
                    console.log(this.getI18nText('sessionModal.loginSuccess', 'Login successful'))
                } else {
                    this.resetLoginButton(submitBtn)
                    if (errorDiv) {
                        errorDiv.textContent = res.msg || this.getI18nText('sessionModal.loginError', 'Login failed')
                        errorDiv.hidden = false
                    }
                }
            })
                .catch(error => {
                    this.isProjectRefreshRequired = false
                    this.reloginToken = ''
                    console.error('Login failure:', error)
                    this.resetLoginButton(submitBtn)

                    if (errorDiv) {
                        let errorMessage = this.getI18nText('sessionModal.loginError', 'Login failed')
                        if (error.message === 'Request timeout') {
                            errorMessage = this.getI18nText('sessionModal.loginTimeout', 'Request timeout, please try again')
                        }
                        errorDiv.textContent = errorMessage
                        errorDiv.hidden = false
                    }
                });
        } catch (error) {
            console.error('Login failure:', error)
            this.resetLoginButton(submitBtn)

            if (errorDiv) {
                errorDiv.textContent = this.getI18nText('sessionModal.loginError', 'Login failed')
                errorDiv.hidden = false
            }
        }
    }


    resetLoginButton(submitBtn) {
        if (submitBtn) {
            submitBtn.disabled = false
            submitBtn.textContent = this.getI18nText('sessionModal.loginButton', 'Login')
            submitBtn.classList.remove('session-lock-button--loading')
        }
    }
}

export default new SessionManagerService()


